import { Avatar, DefaultStreamChatGenerics, useChatContext } from 'stream-chat-react';
import './AvatarChannelView.css';
import { CircularUser } from '../../assets/CircularUser';
import { Channel } from 'stream-chat';
import { StreamChatGenerics } from '../../types';
import { ChatPageType } from '../../enums/ChatPageType';
import { GroupIcon } from '../../assets/GroupIcon';

/** Generates initials from user's full name */
function getNameInitials(
  username:string,
) {
  let names = "";
  if (username && username.trim() !== "") {
      // Split name by spaces
      const tempUserName = username.trim().split(' ');
      // Get first character of first word
      names = tempUserName[0].charAt(0);
      // If multiple words, add first character of last word
      if (tempUserName.length > 1) {
          names += tempUserName[tempUserName.length - 1].charAt(0);
      }
  }
  return names.toUpperCase();
}

export const AvatarChannelView = ({data,pageType}: { data:Channel,pageType:ChatPageType}) => {
  /**
   * HOOK: useChatContext
   * Get Stream Chat client to access current user
   * Needed to filter out current user from members list
   */
  const { client } = useChatContext<DefaultStreamChatGenerics>();
  
  /**
   * JSX element to render as avatar
   * Will be either:
   * - <h4> with initials
   * - <Avatar> with image
   * - <GroupIcon> SVG
   */
  let content: JSX.Element;
  
  /**
   * GET OTHER MEMBERS
   * For DM channels, get the other user (not current user)
   * STEPS:
   * 1. Get all members from channel state
   * 2. Filter out current user
   * 3. Result: array with other user(s)
   * DM CHANNELS:
   * members[0] is the other user in conversation
   */
  const members: any = Object.values(data.state.members || {}).filter(
    (member) => member.user?.id !== client?.user?.id,
  );
  
  /**
   * DETERMINE AVATAR CONTENT
   * Logic branches based on channel type
   */
  if (data.type == "messaging") {
    /**
     * MESSAGING CHANNEL (DM)
     * Show other user's avatar
     * PRIORITY:
     * 1. User's photourl (profile photo)
     * 2. User's name initials (fallback)
     */
    var imageURL: string= members[0]?.user.photourl||undefined;
    
    if(imageURL ===undefined || imageURL === null) {
      // No photo: Generate initials from name
      var name = getNameInitials(members[0]?.user!.name!!);
      content = <h4>{name}</h4>
    }
    else {
      // Has photo: Use Stream Chat's Avatar component
      content = <Avatar image={imageURL} />;
    }
  }
  else {
    /**
     * GROUP CHANNEL (public_with_membership, etc.)
     * Show channel image or group icon
     * CHANNEL DETAILS:
     * Custom channel data stored as JSON string in data.details
     * Contains ImageUrl and other channel metadata
     */
    const channelDetails = JSON.parse(data.data?.details);
    
    if(channelDetails.ImageUrl !== undefined && channelDetails.ImageUrl !== null && channelDetails.ImageUrl !== "") {
      // Has channel image: Use Stream Chat's Avatar component
      content = <Avatar image={channelDetails.ImageUrl} />;
    }
    else{
      // No channel image: Use default group icon
      content = <GroupIcon />
    }
  }
  
  /**
   * RENDER
   * 
   * Wraps content in div with context-specific classes
   * 
   * CSS CLASSES (complex logic):
   * 
   * BASE CLASSES:
   * - Always: 'avatar-group'
   * 
   * CONDITIONAL CLASSES:
   * 1. 'channel_header' if:
   *    - (ChannelDetail page AND messaging type) OR
   *    - (ChannelListing page AND NOT messaging type)
   * 2. 'channel_listing' otherwise
   * 
   * 3. 'background-transparent' if:
   *    - ChannelListing page AND NOT messaging type
   * 
   * WHY THIS LOGIC:
   * Different styling for:
   * - DM in header (larger, circular)
   * - DM in list (smaller, circular)
   * - Group in header (larger, can be square)
   * - Group in list (medium, transparent background)
   */
  return (
    <div className={
      `${(pageType === ChatPageType.ChannelDetail && data.type === "messaging") ||
        (pageType === ChatPageType.ChannelListing && data.type !== "messaging")
        ? 'avatar-group channel_header'
        : 'avatar-group channel_listing'}
      ${pageType === ChatPageType.ChannelListing && data.type !== "messaging" ? 'background-transparent' : ''}`
    }>
      {content}
    </div>
  );
};
export default AvatarChannelView;
